/**
 * The rental fee actually printed on a model card, labelled "-tól" ("from").
 *
 * Two modes, because the two kinds of list make different promises:
 *
 * - Default ("-tól" / from): scans every contract length/mileage cell the model carries and returns
 *   the lowest one, ignoring whatever term is selected in the store. The term selector still
 *   filters WHICH cars are listed and the initial-payment setting still lowers the number, but the
 *   "from" price itself is always the cheapest cell available. At the default 0% initial payment
 *   this equals the server's `min_price_net`, since that's defined as the cheapest cell too.
 *
 * - `options.exactTerm` (rental-fee search only): returns the selected contract length/mileage cell
 *   verbatim. That page filters and orders on that exact cell server-side, so printing the cheapest
 *   cell instead put a price on the card that contradicted the band the visitor was browsing.
 *
 * Kept here rather than in ModelCard.vue because SimilarModelsScroller has to sort by the very same
 * number it renders: the server can only order by a fixed column, but the visitor can change
 * initial payment at runtime, and the initial-payment term below varies per model (via m / prinp2).
 * Two copies of this formula would drift.
 *
 * Returns null when the model has no price cells at all and no min_price_net fallback.
 */
export function calculateModelPrice(model, options) {
  if (!model) {
    return null;
  }

  var mparam = model.m ? model.m : 0.5;
  var initialPaymentPercent = options.initialPaymentPercent || 0;
  var prinp2 = options.prinp2 || 0;

  // The rental-fee search both filters and sorts on one specific price cell, so the card there has
  // to print that same cell — otherwise a car listed inside the 125-175k band advertises its
  // cheapest 98k cell and the list looks wrong. Only that page passes exactTerm (client R3).
  // Falls through to the cheapest-cell scan when the selected cell is missing for this model.
  if (options.exactTerm) {
    var selectedLength = options.contractLength;
    var selected = model['price_' + selectedLength + '_' + options.annualMileage];

    if (selected) {
      return (selected * selectedLength - (initialPaymentPercent / 100) * prinp2 * mparam) / selectedLength;
    }
  }

  var contractLengths = [24, 36, 48, 60];
  var annualMileages = [10, 20, 30, 40, 50];
  var cheapest = null;

  for (var i = 0; i < contractLengths.length; i++) {
    var length = contractLengths[i];

    for (var j = 0; j < annualMileages.length; j++) {
      var mileage = annualMileages[j];
      var cell = model['price_' + length + '_' + mileage];

      if (!cell) {
        continue;
      }

      var adjusted = (cell * length - (initialPaymentPercent / 100) * prinp2 * mparam) / length;

      if (cheapest === null || adjusted < cheapest) {
        cheapest = adjusted;
      }
    }
  }

  if (cheapest === null) {
    return model.min_price_net === undefined ? null : model.min_price_net;
  }

  return cheapest;
}
